import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DAY_NAMES,
  useFoodMenu,
  useUpsertFoodMenu,
  type DayMenu,
  type MealSlot,
} from "@/hooks/useFoodMenu";
import { DayMenuPanel } from "./DayMenuPanel";

const DEFAULT_SLOT: MealSlot = {
  label: "",
  timeOfDay: "",
  items: [],
  sortOrder: 0,
  allowOthers: false,
  isAvailable: true,
  servingCapacity: 4,
};

const buildDefaultDays = (): DayMenu[] =>
  [0, 1, 2, 3, 4, 5, 6].map((d) => ({
    dayOfWeek: d,
    slots: [{ ...DEFAULT_SLOT }],
  }));

interface Props {
  businessId: string;
}

export const FoodMenuTab = ({ businessId }: Props) => {
  const { data, isLoading } = useFoodMenu(businessId);
  const upsert = useUpsertFoodMenu(businessId);
  const [days, setDays] = useState<DayMenu[]>(buildDefaultDays);
  const [activeDay, setActiveDay] = useState("1");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      const filled = [0, 1, 2, 3, 4, 5, 6].map((d) => {
        const found = data.days.find((dm) => dm.dayOfWeek === d);
        return found ?? { dayOfWeek: d, slots: [] };
      });
      setDays(filled);
      setDirty(false);
    }
  }, [data]);

  const updateDay = (updated: DayMenu) => {
    setDays((prev) =>
      prev.map((d) => (d.dayOfWeek === updated.dayOfWeek ? updated : d)),
    );
    setDirty(true);
  };

  const handleCopyDay = (fromDayOfWeek: number) => {
    const source = days.find((d) => d.dayOfWeek === fromDayOfWeek);
    if (!source) return;
    const targetDay = Number(activeDay);
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === targetDay
          ? { ...d, slots: source.slots.map((s) => ({ ...s })) }
          : d,
      ),
    );
    setDirty(true);
  };

  const onSave = async () => {
    const normalized = days.map((d) => ({
      ...d,
      slots: d.slots.map((s, i) => ({ ...s, sortOrder: i })),
    }));
    try {
      await upsert.mutateAsync(normalized);
      toast.success("Food menu saved");
      setDirty(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Save failed");
    }
  };

  const onCancel = () => {
    if (data) {
      const filled = [0, 1, 2, 3, 4, 5, 6].map((d) => {
        const found = data.days.find((dm) => dm.dayOfWeek === d);
        return found ?? { dayOfWeek: d, slots: [] };
      });
      setDays(filled);
    } else {
      setDays(buildDefaultDays());
    }
    setDirty(false);
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground p-4">Loading…</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Weekly meal schedule provided to mothers. Configure slots per day.
      </p>

      <Tabs value={activeDay} onValueChange={setActiveDay}>
        <TabsList className="grid grid-cols-7 w-full">
          {DAY_NAMES.map((name, d) => (
            <TabsTrigger key={d} value={String(d)}>
              {name}
            </TabsTrigger>
          ))}
        </TabsList>

        {[0, 1, 2, 3, 4, 5, 6].map((d) => (
          <TabsContent key={d} value={String(d)} className="pt-4">
            <DayMenuPanel
              day={days[d] ?? { dayOfWeek: d, slots: [] }}
              onUpdate={updateDay}
              onCopyFrom={handleCopyDay}
            />
          </TabsContent>
        ))}
      </Tabs>

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={!dirty || upsert.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onSave}
          disabled={!dirty || upsert.isPending}
        >
          {upsert.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
};
